import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import GlobalSearch from '../../../../src/components/sharedChild/grid/globalSearch';
import { Link, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAccountsDropdown,
  getActiveOwners,
  getGenericMasterFilteredListByKey,
  getGridDataByModuleKey,
  getQuoteList,
  getUser,
} from '../../../redux/selector';
import { columnTypes } from '../../../shared/initials';
import gridOptionsConfig from '../../../components/sharedChild/grid/gridOptionsConfig';
import useFetchIfNeeded from '../../../redux/useFetchIfNeeded';
import { fetchDataAndUpdateStore } from '../../../redux/actions';
import { updateGridColumnState } from '../../../redux/reducer';
import { dropdownPayload, quoteInputData } from '../../../shared/constants';
import GridSkeleton from '../../../shared/component/GridSkeleton';
import { isLoadingByKey } from '../../../shared/controls/methods/getLoadingState';
import useQuoteColumnDefs from './useQuoteColumnDefs';
import apiServices from '../../../services/service';
import { toast } from 'react-toastify';
import FilterHandler from '../../../components/sharedChild/grid/filterHandler';
import LeadsColumns from '../../../components/leads/LeadsColumns';
import LeadsFilters, {
  buildAgFilterModel,
  defaultCond,
  condActive,
  ActiveFilterBar,
} from '../../../components/leads/LeadsFilters';
import QuoteCreateModal from './QuoteCreateModal';

const Dashboard = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(stateData, 'quoteList');
  const navigate = useNavigate();
  const quoteData = useSelector(getQuoteList);
  const [rowData, setRowData] = useState(quoteData);
  const gridOptions = gridOptionsConfig('quoteList', navigate);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const [loading, setLoading] = useState(storeLoading);
  const gridData = useSelector(getGridDataByModuleKey('quoteList'));
  const dispatch = useDispatch();
  const gridRef = useRef();
  const accountDropdown = useSelector(getAccountsDropdown);
  const owner = useSelector(getActiveOwners);
  const [showCreate, setShowCreate] = useState(false);
  const [gridParams, setGridParams] = useState(null);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useFetchIfNeeded(getQuoteList, fetchDataAndUpdateStore, {
    key: 'quoteList',
    inputData: quoteInputData,
  });

  useFetchIfNeeded(getAccountsDropdown, fetchDataAndUpdateStore, {
    key: 'accountDropdown',
    inputData: dropdownPayload,
  });

  const columnDefs = useQuoteColumnDefs({
    accountDropdown,
    owner,
    gridData,
  });

  const defaultColDef = useMemo(() => ({
    sortable: true,
    editable: false,
    flex: 1,
    minWidth: 130,
    filter: true,
    resizable: true,
    enableValue: true,
    enableRowGroup: true,
    enablePivot: true,
  }), []);

  const popupParent = useMemo(() => document.body, []);
  const sortingOrder = useMemo(() => ['desc', 'asc', null], []);

  // ---- custom Filters panel (drives AG Grid filterModel = server-side, via handleFilterChanged) ----
  const [filterConds, setFilterConds] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const filterFields = useMemo(
    () => [
      { field: 'accountName', label: 'Account Name', type: 'set', vals: (accountDropdown || []).map((x) => x.name).filter(Boolean) },
      { field: 'quoteOwner', label: 'Quote Owner', type: 'set', vals: (owner || []).map((x) => x.display_name).filter(Boolean) },
      { field: 'code', label: 'Code', type: 'text' },
      { field: 'subject', label: 'Subject', type: 'text' },
      { field: 'grandTotal', label: 'Total Amount', type: 'number', prefix: '$' },
      { field: 'validUntil', label: 'Valid Until', type: 'date' },
      { field: 'createdDateTime', label: 'Created Date', type: 'date' },
    ],
    [accountDropdown, owner]
  );
  const applyFilterConds = useCallback(
    (c) => {
      const api = gridRef.current?.api;
      if (api) api.setFilterModel(buildAgFilterModel(c, filterFields));
    },
    [filterFields]
  );
  const setFilterCond = (field, patch) => {
    setFilterConds((prev) => {
      const meta = filterFields.find((x) => x.field === field);
      const base = prev[field] || defaultCond(meta.type);
      return { ...prev, [field]: { ...base, type: meta.type, ...patch } };
    });
  };
  const removeFilterCond = (field) => {
    setFilterConds((prev) => {
      const n = { ...prev };
      delete n[field];
      applyFilterConds(n);
      return n;
    });
    setExpandedFilter((e) => (e === field ? null : e));
  };
  const clearAllFilters = () => {
    setFilterConds({});
    applyFilterConds({});
    setExpandedFilter(null);
  };
  const hasActiveFilters = Object.values(filterConds).some(condActive);

  // ---- "Columns" show/hide menu (drives AG Grid column visibility) ----
  const [colsOpen, setColsOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const columnMenuList = useMemo(
    () =>
      columnDefs
        .filter((c) => c.field && c.headerName && c.field !== 'actions')
        .map((c) => ({ id: c.field, label: c.headerName, locked: c.field === 'code' })),
    [columnDefs]
  );
  const syncHiddenCols = useCallback(() => {
    const colApi = gridRef.current?.columnApi;
    if (!colApi?.getColumnState) return;
    setHiddenCols(new Set(colApi.getColumnState().filter((c) => c.hide).map((c) => c.colId)));
  }, []);
  const toggleCol = (id) => {
    const colApi = gridRef.current?.columnApi;
    const api = gridRef.current?.api;
    const makeVisible = hiddenCols.has(id);
    if (colApi?.setColumnsVisible) colApi.setColumnsVisible([id], makeVisible);
    else if (colApi?.setColumnVisible) colApi.setColumnVisible(id, makeVisible);
    else if (api?.setColumnsVisible) api.setColumnsVisible([id], makeVisible);
    syncHiddenCols();
  };
  const toggleAllCols = () => {
    const colApi = gridRef.current?.columnApi;
    const api = gridRef.current?.api;
    const toggleable = columnMenuList.filter((c) => !c.locked).map((c) => c.id);
    if (toggleable.length === 0) return;
    const allShown = toggleable.every((id) => !hiddenCols.has(id));
    const makeVisible = !allShown;
    if (colApi?.setColumnsVisible) colApi.setColumnsVisible(toggleable, makeVisible);
    else if (api?.setColumnsVisible) api.setColumnsVisible(toggleable, makeVisible);
    syncHiddenCols();
  };

  const handleColumnChanged = () => {
    const columnApi = gridRef.current?.columnApi;
    if (!columnApi) return;
    dispatch(
      updateGridColumnState({
        moduleKey: 'quoteList',
        columnState: columnApi.getColumnState(),
      })
    );
    syncHiddenCols();
  };

  const handleGridReady = (params) => {
    gridRef.current = params;
    setGridParams(params);
    const columnOrderFromStore = gridData?.columnState;
    if (columnOrderFromStore) {
      params.columnApi.applyColumnState({ state: columnOrderFromStore, applyOrder: true });
    }
    syncHiddenCols();
  };

  const onFirstDataRendered = useCallback(() => {
    gridRef.current?.api?.paginationGoToPage();
    syncHiddenCols();
  }, [syncHiddenCols]);

  const exportAll = async () => {
    setLoading(true);
    const fetchData = {
      ...quoteInputData,
      pageSize: gridData?.totalCount,
      searchText: gridData?.inputPayload?.searchText ? gridData?.inputPayload?.searchText : null,
      filterJson: gridData?.inputPayload?.filterJson ? gridData?.inputPayload?.filterJson : null,
    };
    try {
      const response = await apiServices.exportAllQuote(fetchData);
      if (response?.data && response.status === 200) {
        setLoading(false);
        if (response?.data.includes('Export initiated.')) {
          toast.warning(response?.data);
          setLoading(false);
        } else {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'Quotes.csv');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        console.error('No data received from API.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error exporting quotes:', error);
      setLoading(false);
    }
  };

  const filterDefaultSetRef = useRef(null);

  // Server-side filter pipeline: AG Grid filterModel -> filterJson -> fetch.
  // (Quote set-filters carry account/owner names, matching the legacy native
  // set-column filters, so the model is passed through unchanged.)
  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
        return;
      }
      const filterModel = event.api.getFilterModel();
      const payload = {
        ...gridData?.inputPayload,
        filterJson: JSON.stringify(filterModel),
        pageNumber: 1,
      };
      dispatch(fetchDataAndUpdateStore({ key: 'quoteList', inputData: payload }));
    },
    [dispatch, gridData?.inputPayload]
  );

  useEffect(() => {
    setRowData(quoteData);
  }, [quoteData]);

  return (
    <div className={`inner-page gos-leads gos-quotes${hasActiveFilters ? ' has-filterbar' : ''}`} style={containerStyle}>
      <div className="dash-container">
        <div className="tbl_container">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-12">
              {/* GrowthOS header */}
              <div className="main-hd">
                <div className="gos-lead-head">
                  <div className="pagetitle">Quotes</div>
                  {gridData?.totalCount !== undefined && (
                    <div className="gos-page-sub">{Number(gridData.totalCount).toLocaleString()} quotes</div>
                  )}
                </div>

                <div className="third-div gos-lead-actions">
                  <button type="button" className="gos-btn gos-btn-ghost" onClick={exportAll}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
                    Export
                  </button>
                  <div className="dropdown gos-more">
                    <button type="button" className="gos-btn gos-btn-primary" id="quoteNewMenu" data-bs-toggle="dropdown" aria-expanded="false">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                      New Quote
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end gos-more-menu" aria-labelledby="quoteNewMenu">
                      <li>
                        <Link className="dropdown-item gos-more-item" to="/quoteAI/regularQuoteAdd">
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" /></svg>
                          Standard Quote
                        </Link>
                      </li>
                      <li>
                        <button type="button" className="dropdown-item gos-more-item" onClick={() => setShowCreate(true)}>
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 8 8 11 4.5-3 8-6 8-11V5l-8-3Z" /></svg>
                          P &amp; C Quote
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* toolbar: search + filters/columns */}
              <div className="gos-lead-toolbar">
                <div className="gos-lead-toolbar-left">
                  <div className="gos-lead-toolbar-search">
                    <GlobalSearch moduleName={'quoteList'} placeholder="Search quotes…" />
                  </div>
                  {gridData?.totalCount !== undefined && (
                    <span className="result-count">
                      <strong>{Number(gridData.totalCount).toLocaleString()}</strong>{' '}
                      {gridData?.inputPayload?.searchText ? 'matches' : 'quotes'}
                    </span>
                  )}
                </div>
                <div className="gos-lead-toolbar-right">
                  <LeadsFilters
                    fields={filterFields}
                    conds={filterConds}
                    setCond={setFilterCond}
                    removeCond={removeFilterCond}
                    clearAll={clearAllFilters}
                    onApply={applyFilterConds}
                    expanded={expandedFilter}
                    setExpanded={setExpandedFilter}
                    open={filterOpen}
                    setOpen={setFilterOpen}
                  />
                  <LeadsColumns
                    columns={columnMenuList}
                    hidden={hiddenCols}
                    onToggle={toggleCol}
                    onToggleAll={toggleAllCols}
                    open={colsOpen}
                    setOpen={setColsOpen}
                  />
                </div>
              </div>

              <ActiveFilterBar
                fields={filterFields}
                conds={filterConds}
                onEdit={(f) => {
                  setExpandedFilter(f);
                  setFilterOpen(true);
                }}
                onRemove={removeFilterCond}
                onClear={clearAllFilters}
              />

              <div className="cutome_ag_theme">
                {loading && <GridSkeleton rows={10} />}
                <div className={`ag-theme-balham tbl_height`} style={{ width: '100%', height: `100%` }}>
                  <AgGridReact
                    className="ag-grid-container"
                    domLayout="fixedHeight"
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    overlayLoadingTemplate={`<Loader />`}
                    animateRows={true}
                    rowHeight={64}
                    headerHeight={44}
                    sideBar={false}
                    enableRangeSelection={true}
                    suppressExcelExport={true}
                    suppressRowClickSelection={true}
                    pivotPanelShow={'never'}
                    rowGroupPanelShow={'never'}
                    popupParent={popupParent}
                    sortingOrder={sortingOrder}
                    onFirstDataRendered={onFirstDataRendered}
                    alwaysShowVerticalScroll={true}
                    gridOptions={gridOptions}
                    columnTypes={columnTypes}
                    onFilterChanged={handleFilterChanged}
                    onGridReady={handleGridReady}
                    onColumnMoved={handleColumnChanged}
                    onColumnVisible={handleColumnChanged}
                  />
                  {gridParams && (
                    <FilterHandler
                      params={gridParams}
                      gridData={gridData}
                      columnDefs={columnDefs}
                      filterDefaultSetRef={filterDefaultSetRef}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCreate && <QuoteCreateModal onClose={() => setShowCreate(false)} />}
    </div>
  );
};

export default Dashboard;
